import { ContentHistory } from "./ContentHistory";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronDown, Loader2, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useContent } from "@/lib/content";
import { ImageField, Loading, PageHead } from "./ui";

const SECTIONS = {
  contact: "Contact details", intro: "Intro video (plays when the site opens)", announcement: "Announcement bar", hero: "Hero (top of page)", treatments: "Treatments cards",
  prices: "Prices section", gallery: "Inspiration gallery", about: "Our story", booking: "Booking section",
  team: "Meet our team", work: "Our work & reviews", promo: "Promo banner (image or video + link)", shop: "Shop page text", visit: "Visit us", newsletter: "Stay in the know", footer: "Footer", policies: "Policies (Terms, Refund & Cancellation, Privacy)",
};
const HINTS = {
  categories: "Service category ids, comma separated (e.g. facials, nails)", match_words: "Also include services whose name contains these words",
  category: "Category id the card opens (e.g. sugaring)", booking_email: "Booking alerts go here; clients' replies go here too", slug: "Page address: /inspiration/<slug>",
  reminder_enabled: "Emails clients at 9 AM the day before their appointment", birthday_enabled: "Emails Birthday Club members at 9 AM on their birthday",
  thankyou_enabled: "Emails clients at 9 AM the day after a confirmed or completed visit", thankyou_button: "Button linking back to the booking form",
  updated: "Set automatically each time you change this policy and save", sections: "Each section has a heading and paragraphs — leave a blank line between paragraphs",
  facebook_url: "Full link to your Facebook page, e.g. https://facebook.com/waxmoi — leave empty to hide the icon",
  instagram_url: "Full link to your Instagram, e.g. https://instagram.com/waxmoi — leave empty to hide the icon",
  tiktok_url: "Full link to your TikTok, e.g. https://tiktok.com/@waxmoi — leave empty to hide the icon",
  review_url: "Your Google review link (Google Business Profile → Ask for reviews). Leave empty to hide", review_label: "Link text for the Google review request",
};
const LINK_HINT = "Where it goes when clicked: /shop, #book, /inspiration/<slug> or a full https:// link. Leave empty for no button";
Object.assign(HINTS, { media_link_url: LINK_HINT, link_url: LINK_HINT, media_link_label: "Button text shown on the photo/video, e.g. Click here to shop now",
  image: "Upload a photo or a video (MP4, WEBM, MOV up to 60 MB)", media: "Upload a photo or a video (MP4, WEBM, MOV up to 60 MB)",
  media_desktop: "Intro video for computers & tablets (MP4, WEBM, MOV up to 60 MB). Plays once per visit, muted with tap-to-unmute",
  media_mobile: "Intro video for phones (portrait works best). Leave empty to skip the intro on phones" });
const TEMPLATES = { members: { name: "", title: "", bio: "", image: "" }, sections: { heading: "", body: "" } };
const nice = (k) => k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const LONG = /text|lead|paragraph|intro|body|lines|note|disclaimer|bio/;
const blank = (v) => (Array.isArray(v) ? [] : typeof v === "object" && v ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, blank(x)])) : typeof v === "string" ? "" : v);

const renderValue = (k, v, onChange, path) => {
  const tid = `content-${path}`;
  if (typeof v === "boolean") return <label className="ad-check"><input type="checkbox" checked={v} onChange={(e) => onChange(e.target.checked)} data-testid={tid} /> {v ? "On" : "Off"}</label>;
  if (k === "updated") return <input value={v} readOnly disabled data-testid={tid} />;
  if (typeof v === "string") {
    if (k.includes("image") || /^media(_desktop|_mobile)?$/.test(k)) return <ImageField value={v} onChange={onChange} testId={tid} />;
    if (LONG.test(k) || v.length > 90) return <textarea value={v} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
    return <input value={v} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
  }
  if (Array.isArray(v)) {
    const set = (i, x) => onChange(v.map((o, j) => (j === i ? x : o)));
    const isObj = typeof v[0] === "object";
    return (
      <div className="ad-array">
        {v.map((item, i) => (
          <div className={isObj ? "ad-array-item" : "ad-array-row"} key={i}>
            {isObj ? renderFields(item, (x) => set(i, x), `${path}-${i}`) : <input value={item} onChange={(e) => set(i, e.target.value)} data-testid={`${tid}-${i}`} />}
            <button type="button" className="ad-icon-btn" onClick={() => onChange(v.filter((_, j) => j !== i))} aria-label="Remove" data-testid={`${tid}-${i}-remove`}><Trash2 size={14} /></button>
          </div>
        ))}
        <button type="button" className="btn light ad-add" onClick={() => onChange([...v, v.length ? blank(v[0]) : TEMPLATES[k] ? { ...TEMPLATES[k] } : ""])} data-testid={`${tid}-add`}><Plus size={14} /> Add</button>
      </div>
    );
  }
  if (v && typeof v === "object") return renderFields(v, onChange, path);
  return null;
};

const renderFields = (obj, onChange, path) => (
  <div className="ad-fields">
    {Object.entries(obj).map(([k, v]) => (
      <div className="field" key={k}>
        <label>{nice(k)}</label>
        {HINTS[k] && <p className="ad-hint">{HINTS[k]}</p>}
        {renderValue(k, v, (x) => onChange({ ...obj, [k]: x }), `${path}-${k}`)}
      </div>
    ))}
  </div>
);

export default function ContentEditor() {
  const { data } = useContent();
  const [draft, setDraft] = useState(null);
  const [open, setOpen] = useState("hero");
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  useEffect(() => { if (data && !draft) setDraft(structuredClone(data)); }, [data, draft]);

  const save = async () => {
    setBusy(true);
    try {
      const { data: saved } = await api.put("/admin/content", { data: draft });
      qc.setQueryData(["content"], saved);
      setDraft(structuredClone(saved));
      qc.invalidateQueries({ queryKey: ["admin", "content-history"] });
      toast.success("Site content saved — live now");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-content">
      <PageHead eyebrow="Edit every section" title="Site content">
        <button className="btn gold" onClick={save} disabled={busy || !draft} data-testid="content-save-button">{busy ? <Loader2 size={16} className="spin" /> : "Save changes"}</button>
      </PageHead>
      <ContentHistory onRestored={(saved) => setDraft(structuredClone(saved))} />
      {!draft ? <Loading /> : Object.entries(SECTIONS).map(([key, title]) => (
        <section className="ad-card ad-section" key={key} data-testid={`content-section-${key}`}>
          <button type="button" className="ad-section-head" onClick={() => setOpen(open === key ? null : key)} aria-expanded={open === key} data-testid={`content-section-${key}-toggle`}>
            {title}<ChevronDown size={18} />
          </button>
          {open === key && renderFields(draft[key], (x) => setDraft({ ...draft, [key]: x }), key)}
        </section>
      ))}
      <div className="ad-savebar"><button className="btn gold" onClick={save} disabled={busy || !draft} data-testid="content-save-bottom-button">Save changes</button></div>
    </div>
  );
}
